import React, { useEffect, useState } from "react";
import { Box, Button, FormControlLabel, IconButton, Modal, Switch, TextField, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import modalStyle from "../../styles/modalStyle";
import { textPattern } from "../../constants/patterns";
import apiClient from "../../axiosClient";
import DropDown from "../baseComponents/dropdown";
import DispositionInput from "../../interfaces/disposition_input";
import CreateDropdownOptions from "../baseComponents/createDropdownOptions";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { DISPOSITION_INPUT_TYPES } from "../../constants/constants";

interface AddDispositionInputModalProps {
    open: boolean;
    onClose: () => void;
    getAPIFunction: () => Promise<void>;
    dispositionID: string;
    selectedDispositionInput?: DispositionInput;
};
const styles = {
    horizontalStyle: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 2 },
    widthStyle: { width: '49%' },
};

const AddDispositionInputModal: React.FC<AddDispositionInputModalProps> = ({ open, onClose, getAPIFunction, dispositionID, selectedDispositionInput }) => {
    const { DISPOSITION_INPUT_TYPE } = useSelector((state: RootState) => state.picklists.picklists);
    const [isMandatory, setIsMandatory] = useState<boolean>(false);
    const [fieldName, setFieldName] = useState<string>('');
    const [fieldLabel, setFieldLabel] = useState<string>('');
    const [config, setConfig] = useState<Record<string, any>>({});
    const [inputType, setInputType] = useState<string>('');

    useEffect(() => {
        if (selectedDispositionInput) {
            setFieldName(selectedDispositionInput.field_name);
            setFieldLabel(selectedDispositionInput.label);
            setConfig(selectedDispositionInput.config || {});
            setIsMandatory(selectedDispositionInput.is_mandatory);
            setInputType(selectedDispositionInput.input_type);
        }
    }, []);

    const handleCloseModal = () => {
        setFieldName('');
        setFieldLabel('');
        setConfig({});
        setIsMandatory(false);
        setInputType('');
        onClose();
    };

    useEffect(() => {
        if (selectedDispositionInput && inputType === selectedDispositionInput.input_type) {
            setConfig(selectedDispositionInput.config || {});
        } else {
            setConfig({});
        }
    }, [inputType]);

    const addOrEditDispositionInput = async () => {
        try {
            if (!fieldLabel || !fieldName || !inputType) {
                alert('Please fill the necessary values');
                return;
            } else if (inputType === DISPOSITION_INPUT_TYPES.dropdown && !(Object.keys(config).length === 1 && Object.values(config)[0])) {
                alert('Please enter the dropdown values correctly');
                return;
            }
            let body: Record<string, any> = {
                disposition_input_id: selectedDispositionInput ? selectedDispositionInput.disposition_input_id : null,
                disposition_id: dispositionID,
                field_name: fieldName,
                label: fieldLabel,
                config: Object.keys(config).length > 0 ? config : null,
                input_type: inputType,
                is_mandatory: isMandatory,
                is_active: true
            };
            let response;
            if (selectedDispositionInput) {
                response = await apiClient.put('/api/v1/disposition_input', body);
            } else {
                response = await apiClient.post('/api/v1/disposition_input', body);
            }
            if (response.status === 200) {
                alert("Disposition Input added successfully");
                handleCloseModal();
                await getAPIFunction();
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        }
    };

    return (
        <Modal open={open} onClose={handleCloseModal}>
            <Box sx={modalStyle}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography id="modal-title" variant="h6">
                        {selectedDispositionInput ? 'Edit' : 'Add New'} Disposition Input
                    </Typography>
                    <IconButton onClick={handleCloseModal}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box sx={styles.horizontalStyle}>
                    <TextField
                        label="Field Name"
                        value={fieldName}
                        onChange={(e) => setFieldName(e.target.value.replace(textPattern, ""))}
                        sx={styles.widthStyle}
                        required
                    />
                    <TextField
                        label="Field Label"
                        value={fieldLabel}
                        onChange={(e) => setFieldLabel(e.target.value.replace(textPattern, ""))}
                        sx={styles.widthStyle}
                        required
                    />
                </Box>
                <DropDown
                    label="Input Type"
                    selectedValue={inputType}
                    onSelection={setInputType}
                    items={DISPOSITION_INPUT_TYPE}
                    style={{ mb: 2 }}
                    required
                />
                {inputType === DISPOSITION_INPUT_TYPES.date ? (
                    <Box sx={styles.horizontalStyle}>
                        <TextField
                            label="Maximum days allowed in the past"
                            value={config?.min ?? ''}
                            onChange={(e) => {
                                if (e.target.value) {
                                    setConfig({ ...config, min: parseInt(e.target.value) })
                                } else {
                                    let finalConfig = { ...config };
                                    delete finalConfig.min;
                                    setConfig(finalConfig);
                                }
                            }}
                            required
                            type="number"
                            sx={styles.widthStyle}
                            fullWidth
                        />
                        <TextField
                            label="Maximum days allowed in the future"
                            value={config?.max ?? ''}
                            onChange={(e) => {
                                if (e.target.value) {
                                    setConfig({ ...config, max: parseInt(e.target.value) })
                                } else {
                                    let finalConfig = { ...config };
                                    delete finalConfig.max;
                                    setConfig(finalConfig);
                                }
                            }}
                            required
                            type="number"
                            sx={styles.widthStyle}
                            fullWidth
                        />
                    </Box>
                ) : inputType === DISPOSITION_INPUT_TYPES.dropdown ? (
                    <CreateDropdownOptions
                        label="Add Options for Dropdown"
                        newOptions={(config?.items || '')?.split(';')}
                        setNewOptions={val => setConfig({ items: val.join(';') })}
                    />
                ) : null}
                <FormControlLabel
                    control={
                        <Switch
                            checked={isMandatory}
                            onChange={e => setIsMandatory(e.target.checked)}
                            name="Is Mandatory"
                            size="medium"
                        />
                    }
                    sx={{ mt: 2 }}
                    label="Will this disposition input be mandatory?*"
                    labelPlacement="end"
                />
                <Box mt={2} textAlign="right">
                    <Button variant="contained" color="primary" onClick={addOrEditDispositionInput}>
                        <Typography>Submit</Typography>
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default AddDispositionInputModal;